import {
  afterRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  HostListener,
  inject,
  Injector,
  OnDestroy,
  TemplateRef,
  viewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ResponsiveAbstractCardComponent } from '@pc/responsive-abstract-card';
import { BreakpointObserverService } from '@pc/shared';
import { EProcessType, PcButtonIcon, ChevronStateIcons } from '@pc/common';
import { ItemsSummaryCardAnimation } from '@pc/items-summary-card';
import { expand } from '@pc/animations';
import { AnimationDialogService } from '@pc/dialog-abstract';
import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { QuoteOverviewPopupComponent } from './quote-overview-popup/quote-overview-popup.component';
import { QuoteOverviewContentComponent } from './quote-overview-content/quote-overview-content.component';
import { QuoteOverviewHeaderComponent } from './quote-overview-header/quote-overview-header.component';
import { QuoteOverviewService } from '../../shared/services/quote/quote-overview.service';
import { OverviewState } from './quote-overview.const';
@Component({
  selector: 'pc-quote-overview',
  standalone: true,
  imports: [CommonModule, ResponsiveAbstractCardComponent, QuoteOverviewContentComponent, QuoteOverviewHeaderComponent],
  templateUrl: './quote-overview.component.html',
  styleUrl: './quote-overview.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [ItemsSummaryCardAnimation, expand]
})
export class QuoteOverviewComponent implements OnDestroy {
  readonly injector = inject(Injector);
  readonly breakpoint = inject(BreakpointObserverService);
  public quoteOverviewService = inject(QuoteOverviewService);

  public mediumBreakpoint = this.breakpoint.getMediumBreakpoint(this.injector);
  public isStandalone = this.breakpoint.getXLargeBreakpoint(this.injector);
  public isMobile = computed(() => !this.mediumBreakpoint());
  public quoteTripDetailsData = computed(() => this.quoteOverviewService.quoteTripDetailsData());
  public quoteCoversData = computed(() => this.quoteOverviewService.quoteCoversData());
  public quoteOverviewLabels = computed(() => this.quoteOverviewService.quoteOverviewLabels());
  public totalPremium = computed(() => this.quoteOverviewService.totalPremium());
  public quoteOverviewState = computed(() => this.getQuoteOverviewState());
  public tripDetailsIcons = computed(() => this.quoteOverviewService.getTripDetailsIcons());
  public chevronIcons = computed(() => this.getChevronIcons());
  public ProcessTypes = EProcessType;
  public processType = computed<EProcessType>(() => this.quoteOverviewService.getProcessType());
  public showEmailSection = computed<boolean>(() => this.quoteOverviewService.showEmailSection());
  public readonly PcButtonIcon = PcButtonIcon;
  public readonly GOODS_AND_SERVICES_TAX_RATE = this.quoteOverviewService.GOODS_AND_SERVICES_TAX_RATE;
  public summaryTemplateRef = viewChild<TemplateRef<unknown | undefined>>('quoteOverviewContent');

  private elementRef = inject(ElementRef);
  private dialog = inject(Dialog);
  private animationDialogService = inject(AnimationDialogService);
  private dialogRef?: DialogRef<unknown, QuoteOverviewPopupComponent>;

  constructor() {
    afterRender(() => {
      if (this.summaryTemplateRef) {
        this.quoteOverviewService.setSummaryTemplateRef(this.summaryTemplateRef());
      }
    });
  }

  @HostListener('document:mousedown', ['$event']) onDocumentClick(event: MouseEvent): void {
    const clickedOutside = !this.elementRef.nativeElement.contains(event.target);
    const shouldCloseOverview = clickedOutside && this.quoteOverviewService.isQuoteOverviewOpen();
    if (shouldCloseOverview) this.quoteOverviewService.updateQuoteOverviewState();
  }

  ngOnDestroy(): void {
    this.closeQuoteOverviewPopup();
  }

  public onQuoteOverviewStateChange(): void {
    if (this.isMobile()) {
      this.openQuoteOverviewPopup();
    } else {
      this.quoteOverviewService.updateQuoteOverviewState();
    }
  }

  public openQuoteOverviewPopup(): void {
    this.dialogRef = this.animationDialogService.openDialogWithAnimation(this.dialog, QuoteOverviewPopupComponent, {
      isMobile: this.isMobile(),
      isStandalone: this.isStandalone(),
      totalPrice: this.totalPremium,
      quoteOverviewLabels: this.quoteOverviewLabels(),
      quoteTripDetailsData: this.quoteTripDetailsData(),
      closeIcon: PcButtonIcon.POPUP_X_CLOSE,
      tripDetailsIcons: this.tripDetailsIcons(),
      quoteCoversData: this.quoteCoversData(),
      privacyPolicyLink: this.quoteOverviewService.privacyPolicyLink()
    });
  }

  public setBrokerFee(fee: number): void {
    this.quoteOverviewService.setBrokerFee(fee);
  }

  private closeQuoteOverviewPopup(): void {
    this.dialogRef?.close();
  }

  private getQuoteOverviewState(): OverviewState {
    return this.quoteOverviewService.isQuoteOverviewOpen() ? OverviewState.Expanded : OverviewState.Collapsed;
  }

  private getChevronIcons(): ChevronStateIcons {
    return {
      up: PcButtonIcon.CHEVRON_UP_BLUE,
      down: PcButtonIcon.CHEVRON_DOWN_BLUE
    };
  }
}
